import type { Database } from '@/integrations/supabase/types';

// Database types
export type CallLog = Database['public']['Tables']['call_logs']['Row'];
export type CallLogInsert = Database['public']['Tables']['call_logs']['Insert'];
export type CallLogUpdate = Database['public']['Tables']['call_logs']['Update'];

// Call direction
export type CallDirection = 'inbound' | 'outbound';

// Call status — matches expanded DB CHECK constraint (Phase 0)
export type CallStatus =
  | 'ringing'
  | 'in_progress'
  | 'answered'
  | 'completed'
  | 'missed'
  | 'hangup'
  | 'busy'
  | 'canceled'
  | 'failed'
  | 'voicemail'
  | 'no_answer';

// =====================================================
// REPORT DISPOSITION BUCKETS (5-bucket model)
// Split "Missed/Unanswered" into Abandoned vs Missed
// per ServiceTitan best practice:
//   Abandoned = capacity fault (need more CSRs)
//   Missed    = agent fault (personnel/behavioral issue)
// =====================================================
export type DispositionBucket =
  | 'answered'    // answered, completed — call connected, conversation happened
  | 'abandoned'   // hangup, busy — capacity fault, caller couldn't get through
  | 'missed'      // missed, no_answer, canceled — agent fault, call routed but not picked up
  | 'voicemail'   // voicemail — went to voicemail
  | 'failed';     // failed — system/network error

export const DISPOSITION_BUCKET_MAP: Record<string, DispositionBucket> = {
  answered:    'answered',
  completed:   'answered',
  hangup:      'abandoned',
  busy:        'abandoned',
  missed:      'missed',
  no_answer:   'missed',
  canceled:    'missed',
  voicemail:   'voicemail',
  failed:      'failed',
};

export const DISPOSITION_BUCKET_LABELS: Record<DispositionBucket, string> = {
  answered:  'Answered',
  abandoned: 'Abandoned',
  missed:    'Missed',
  voicemail: 'Voicemail',
  failed:    'Failed',
};

export const DISPOSITION_BUCKET_COLORS: Record<DispositionBucket, string> = {
  answered:  'text-success',
  abandoned: 'text-destructive',
  missed:    'text-warning',
  voicemail: 'text-info',
  failed:    'text-gray-500',
};

/** Resolve a raw call_logs.status into a report bucket */
export function getDispositionBucket(status: string | null | undefined): DispositionBucket {
  if (!status) return 'missed';
  return DISPOSITION_BUCKET_MAP[status] ?? 'missed';
}

// SMS status — matches expanded DB CHECK constraint (Phase 0)
export type SmsStatus =
  | 'sending'
  | 'queued'
  | 'in_progress'
  | 'sent'
  | 'delivered'
  | 'received'
  | 'receiving'
  | 'failed'
  | 'unsent'
  | 'undelivered'
  | 'unreachable'
  | 'blocked'
  | 'flagged'
  | 'no_answer';

// Call disposition options (manual CRM disposition, not CTM status)
export type CallDisposition =
  | 'booked'
  | 'callback'
  | 'no_answer'
  | 'not_interested'
  | 'wrong_number'
  | 'voicemail'
  | 'other';

// Extended call log with relations
export interface CallLogWithRelations extends CallLog {
  leads?: { customer_name: string; lead_number: string } | null;
  customers?: { customer_name: string } | null;
  campaigns?: { name: string } | null;
  profiles?: { full_name: string } | null;
}

// CTM recording response
export interface CTMRecordingResponse {
  url: string;
  expires_at: string;
  duration_seconds: number;
}

// CTM webhook event types
export type CTMEventType =
  | 'start'
  | 'end'
  | 'voicemail'
  | 'recording_ready'
  | 'inbound_text'
  | 'outbound_text'
  | 'status_change';

// CTM webhook payload (partial - add fields as needed)
export interface CTMWebhookPayload {
  id: string;
  event_type: CTMEventType;
  account_id: string;
  tracking_number?: string;
  caller_number?: string;
  receiver_number?: string;
  duration?: number;
  talk_time?: number;
  recording_id?: string;
  started_at?: string;
  answered_at?: string;
  ended_at?: string;
  // SMS-specific fields
  from_number?: string;
  to_number?: string;
  content?: string;
  media_urls?: string[];
  status?: string;
  // Additional metadata
  [key: string]: unknown;
}
